import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import SubscriberLayout from '@/components/layout/SubscriberLayout';
import { API, useAuth } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ChevronLeft, ChevronRight, Save, Send, Loader2, CheckCircle2, Sparkles,
} from 'lucide-react';
import { MASTER_INTAKE_SCHEMA } from '@/schemas/intake_schemas';
import {
  SchemaSection, SectionNav, validateSchema, overallCompleteness,
} from '@/components/forms/SchemaFormRenderer';

const AUTOSAVE_MS = 2500;

export default function MyIntake() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [status, setStatus] = useState('draft');

  const [answers, setAnswers] = useState({});
  const [uploads, setUploads] = useState({});
  const [activeKey, setActiveKey] = useState(MASTER_INTAKE_SCHEMA[0].key);

  const dirty = useRef(false);
  const saveTimer = useRef(null);
  const lastSavedAt = useRef(null);

  // ── Load existing draft ──
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/master-intake/my-draft`);
        if (!mounted) return;
        setAnswers(r.data?.answers || {});
        setUploads(r.data?.uploads || {});
        setStatus(r.data?.status || 'draft');
        setSubmittedAt(r.data?.submitted_at || null);
      } catch (e) {
        console.error('Failed to load draft', e);
        toast.error('Could not load your intake. Please refresh.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // ── Save draft (debounced) ──
  const saveDraft = useCallback(async (silent = false) => {
    if (!dirty.current) return;
    try {
      setSaving(true);
      await axios.put(`${API}/master-intake/save-draft`, {
        answers, uploads,
      });
      dirty.current = false;
      lastSavedAt.current = new Date();
      if (!silent) toast.success('Draft saved');
    } catch (e) {
      console.error('save failed', e);
      if (!silent) toast.error('Could not save your draft');
    } finally {
      setSaving(false);
    }
  }, [answers, uploads]);

  // ── Debounced autosave on change ──
  useEffect(() => {
    if (loading) return;
    dirty.current = true;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveDraft(true), AUTOSAVE_MS);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [answers, uploads, loading, saveDraft]);

  // ── Section change handler ──
  const onSectionChange = (sectionKey, sectionAnswers) => {
    setAnswers(prev => ({ ...prev, [sectionKey]: sectionAnswers }));
    // Mirror file-type fields into uploads map for backend traceability
    const sect = MASTER_INTAKE_SCHEMA.find(s => s.key === sectionKey);
    if (sect) {
      const filePatch = {};
      for (const f of sect.fields || []) {
        if (f.type === 'file') {
          filePatch[f.key] = sectionAnswers[f.key] || null;
        }
      }
      if (Object.keys(filePatch).length > 0) {
        setUploads(prev => ({ ...prev, ...filePatch }));
      }
    }
  };

  const activeSection = useMemo(
    () => MASTER_INTAKE_SCHEMA.find(s => s.key === activeKey) || MASTER_INTAKE_SCHEMA[0],
    [activeKey]
  );
  const activeIndex = MASTER_INTAKE_SCHEMA.findIndex(s => s.key === activeKey);
  const isLast = activeIndex === MASTER_INTAKE_SCHEMA.length - 1;
  const isFirst = activeIndex === 0;

  const completeness = overallCompleteness(MASTER_INTAKE_SCHEMA, answers);
  const errors = validateSchema(MASTER_INTAKE_SCHEMA, answers);

  // ── Submit ──
  const submit = async () => {
    if (errors.length > 0) {
      toast.error(`Please complete required fields: ${errors.slice(0, 2).map(e => e.label).join(', ')}${errors.length > 2 ? '…' : ''}`);
      setActiveKey(errors[0].section);
      return;
    }
    try {
      setSubmitting(true);
      if (dirty.current) await saveDraft(true);
      const r = await axios.post(`${API}/master-intake/submit`);
      setStatus(r.data?.status || 'submitted');
      setSubmittedAt(new Date().toISOString());
      toast.success('Intake submitted — thank you.');
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const goPrev = () => !isFirst && setActiveKey(MASTER_INTAKE_SCHEMA[activeIndex - 1].key);
  const goNext = () => !isLast && setActiveKey(MASTER_INTAKE_SCHEMA[activeIndex + 1].key);

  if (loading) {
    return (
      <SubscriberLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-6 w-6 animate-spin text-[#C89B7B]" />
        </div>
      </SubscriberLayout>
    );
  }

  const isSubmitted = status === 'submitted';

  return (
    <SubscriberLayout>
      <div className="max-w-6xl mx-auto p-4 md:p-6" data-testid="master-intake-page">
        {/* Header */}
        <div className="mb-4 md:mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-[#1C1A19] tracking-tight">
                Your Master Intake
              </h1>
              <p className="text-sm text-[#5C5955] mt-1 max-w-2xl">
                A one-time assessment that maps your Biological (BAS), Neurological (NAS),
                and Terrain & Toxic (TAS) arrival state. Your answers are saved as you type.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                variant="secondary"
                className={isSubmitted ? 'bg-[#E7EFE9] text-[#2F5A40]' : 'bg-[#F5EEDC] text-[#7A5A2B]'}
                data-testid="intake-status-badge"
              >
                {isSubmitted ? 'Submitted' : 'Draft'}
              </Badge>
              <Badge variant="outline" className="border-[#C89B7B] text-[#7A4A2B]" data-testid="intake-completeness">
                {completeness}% complete
              </Badge>
            </div>
          </div>

          {isSubmitted ? (
            <div className="mt-4 rounded-md border border-[#CFE3D6] bg-[#F2F8F3] p-3 flex items-start gap-2" data-testid="intake-submitted-banner">
              <CheckCircle2 className="h-4 w-4 text-[#2F5A40] mt-0.5" />
              <div>
                <p className="text-sm text-[#1F3B2A] font-medium">Your intake has been submitted.</p>
                <p className="text-xs text-[#3F604C] mt-0.5">
                  Your care team has been notified. You can continue to refine answers; any future submission will update your record.
                </p>
              </div>
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 md:gap-6">
          {/* Left section nav */}
          <aside className="md:sticky md:top-4 md:self-start" data-testid="intake-nav">
            <Card className="bg-[#FBFAF7] border-[#EBE8E3]">
              <CardContent className="p-3">
                <SectionNav
                  schema={MASTER_INTAKE_SCHEMA}
                  activeKey={activeKey}
                  answers={answers}
                  onSelect={setActiveKey}
                  tidPrefix="mi"
                />
              </CardContent>
            </Card>
          </aside>

          {/* Active section */}
          <main>
            <Card className="bg-white border-[#EBE8E3]">
              <CardContent className="p-5 md:p-7">
                <div className="flex items-center justify-between gap-3 mb-5">
                  <div>
                    <div className="flex items-center gap-2 text-xs text-[#8A8681]">
                      <Sparkles className="h-3 w-3" />
                      <span>Section {activeIndex + 1} of {MASTER_INTAKE_SCHEMA.length}</span>
                    </div>
                    <h2 className="text-xl font-semibold text-[#1C1A19] mt-1" data-testid="active-section-title">
                      {activeSection.title}
                    </h2>
                  </div>
                  {saving ? (
                    <span className="text-xs text-[#8A8681] flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" /> Saving…
                    </span>
                  ) : (
                    <span className="text-xs text-[#4E7A6B]">All changes saved</span>
                  )}
                </div>

                <SchemaSection
                  section={activeSection}
                  answers={answers}
                  onSectionChange={onSectionChange}
                  tidPrefix="mi"
                />

                {/* Footer nav */}
                <div className="mt-7 flex items-center justify-between gap-3">
                  <Button
                    variant="outline"
                    onClick={goPrev}
                    disabled={isFirst}
                    className="border-[#D6D1C8]"
                    data-testid="intake-prev-btn"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                  </Button>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => saveDraft(false)}
                      disabled={saving}
                      className="border-[#D6D1C8]"
                      data-testid="intake-save-btn"
                    >
                      {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                      Save draft
                    </Button>

                    {isLast ? (
                      <Button
                        onClick={submit}
                        disabled={submitting}
                        className="bg-[#2C2A29] hover:bg-[#1C1A19] text-white"
                        data-testid="intake-submit-btn"
                      >
                        {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                        {isSubmitted ? 'Resubmit' : 'Submit intake'}
                      </Button>
                    ) : (
                      <Button
                        onClick={goNext}
                        className="bg-[#2C2A29] hover:bg-[#1C1A19] text-white"
                        data-testid="intake-next-btn"
                      >
                        Next <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    )}
                  </div>
                </div>

                {errors.length > 0 && isLast ? (
                  <div className="mt-4 rounded-md border border-[#F0D5CF] bg-[#FDF4F2] p-3 text-xs text-[#8A3B2D]" data-testid="intake-errors">
                    <p className="font-medium mb-1">Please complete these before submitting:</p>
                    <ul className="list-disc ml-4 space-y-0.5">
                      {errors.slice(0, 5).map(er => (
                        <li key={`${er.section}.${er.field}`}>
                          <button type="button" onClick={() => setActiveKey(er.section)} className="underline">
                            {er.sectionTitle}: {er.label}
                          </button>
                        </li>
                      ))}
                      {errors.length > 5 ? <li>…and {errors.length - 5} more</li> : null}
                    </ul>
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <p className="text-[11px] text-[#8A8681] mt-3 text-center">
              This assessment maps your biological arrival state. It is not a medical diagnosis.
            </p>
          </main>
        </div>
      </div>
    </SubscriberLayout>
  );
}
